import { DialogActions, DialogContent, DialogContentText, DialogTitle, Stack } from '@montugroup/design-system';
import CloseIcon from '@mui/icons-material/Close';
import { useId } from 'react';

import type { PostBookingTrigger } from '@/hooks/analytics/useConsultationWaitlistTracking';
import type { UsePostBookingWaitlistPromptOptions } from '@/pages/consultations/hooks/usePostBookingWaitlistPrompt';
import { usePostBookingWaitlistPrompt } from '@/pages/consultations/hooks/usePostBookingWaitlistPrompt';
import { DialogCloseButton, WaitlistDialog } from './PostBookingWaitlistDialog.styles';
import { isWaitlistRejoin, WAITLIST_NOT_ELIGIBLE_MESSAGE, waitlistJoinMessage } from './waitlist.config';
import { WaitlistPreferenceForm } from './WaitlistPreferenceForm';
import { ActionButton } from './WaitlistPreferenceForm.styles';

export type PostBookingWaitlistDialogProps = UsePostBookingWaitlistPromptOptions;

const HEADING: Record<PostBookingTrigger, string> = {
  booking: 'Thanks for booking',
  reschedule: 'Your appointment has been rescheduled',
};

/**
 * Offers the waitlist after a booking or reschedule; mount with a new key per booking. It only appears once the patient
 * can join, and otherwise closes unseen, leaving the card to show the other states (e.g. asking for a mobile).
 */
export function PostBookingWaitlistDialog(props: PostBookingWaitlistDialogProps) {
  const titleId = useId();
  const { consultation, waitlist, canJoin, isFormShown, joinOutcome, isPending, errorMessage, join, dismiss, close } =
    usePostBookingWaitlistPrompt(props);

  // canJoin already means both are present; this narrows them
  if (!canJoin || !consultation || !waitlist) {
    return null;
  }

  return (
    <WaitlistDialog open onClose={isPending ? undefined : dismiss} aria-labelledby={titleId} disableScrollLock>
      {/* the dialog is named by its title, so it's announced as focus moves into the form */}
      <DialogTitle id={titleId}>{HEADING[props.prompt.trigger]}</DialogTitle>
      <DialogCloseButton aria-label="Close dialog" onClick={dismiss} disabled={isPending}>
        <CloseIcon />
      </DialogCloseButton>
      <DialogContent aria-live="polite">
        {joinOutcome === 'joined' && <DialogContentText>You&apos;re on the waitlist.</DialogContentText>}
        {joinOutcome === 'notEligible' && <DialogContentText>{WAITLIST_NOT_ELIGIBLE_MESSAGE}</DialogContentText>}
        {isFormShown && (
          <Stack gap={6}>
            <DialogContentText>
              {waitlistJoinMessage(isWaitlistRejoin(waitlist), consultation.practitioner?.name)}
            </DialogContentText>
            <WaitlistPreferenceForm
              initialAvailability={waitlist.availability}
              submitLabel="Join waitlist"
              cancelLabel="No thanks"
              isSubmitting={isPending}
              errorMessage={errorMessage}
              onSubmit={join}
              onCancel={dismiss}
            />
          </Stack>
        )}
      </DialogContent>
      {!isFormShown && joinOutcome !== 'joined' && (
        <DialogActions>
          <ActionButton variant="contained" color="primary" onClick={close}>
            Close
          </ActionButton>
        </DialogActions>
      )}
    </WaitlistDialog>
  );
}
